import { useGongFlowStepsForced } from './gong-flow-queries';
import { StepOverrideEditor, type StepOverrideEditorLabels } from './StepOverrideEditor';
import { useBatchStepBuffer } from './useBatchStepBuffer';
import type { IdentityKey, PlayKey } from './state/staged-play-config';

/**
 * Batch-wide "Flow step defaults" for a `gong_flow` group's Bulk Editor
 * (plan 2026-09-08-002 Part 2 — the v3 rewrite onto the canonical staged-play
 * rail).
 *
 * The rep types default step copy ONCE and every not-individually-touched
 * lead's own step editor reflects it LIVE — no "Apply" button, no click, no
 * snapshot bookkeeping. This is the exact behavior the base-copy Bulk
 * Editor's Base subject/Base body fields already have
 * (`BaseCopyEditor.tsx`); this component is the `gong_flow` group's
 * equivalent for per-step copy, needed because `BaseCopyEditor` gates its
 * two copy fields on `hasEditableCopy`, which `gong_flow` sets false by
 * construction (`GONG_FLOW_INPUT_SCHEMA` has no `input.*`-sourced field) —
 * without this, a rep bulk-adding 30 contacts to one Gong flow retypes the
 * same step copy 30 times.
 *
 * v3 deletes the card-held sentinel `StepOverrideStore` this component used
 * to write into (Devin's review on PR #1390, 2026-09-09 — a second rail next
 * to the codebase's canonical one for exactly this concept) and writes
 * DIRECTLY to `state/staged-play-config.ts`'s `stepCopy` field instead,
 * through the SAME `setStagedStepCopy`/`clearStagedStepCopy` mutators
 * `PlayQueueCard` reads back via `useStagedStepCopy` and threads to every
 * follower row as a plain data prop (`StepOverrideEditor.tsx`'s
 * `batchStepCopy`). This buys, for free, everything `staged-play-config.ts`
 * already provides: tab-close/reload survival (zustand `persist`),
 * cross-tab sync, and post-send pruning (`reconcileStagedPlayConfig`) — none
 * of which the deleted sentinel store had.
 *
 * ── The typing buffer (Key Technical Decision 4) ─────────────────────────
 * Mirrors `BaseCopyEditor.tsx`'s `BASE_COPY_COMMIT_MS` buffer exactly (see
 * that file's own doc comment for the full rationale — the staged store is
 * PERSISTED, so a raw per-keystroke write is a `JSON.stringify` of the whole
 * identity bucket plus `localStorage.setItem`, and the read channel is
 * card-level, so a raw write also re-renders `PlayQueueCard` and every row
 * in it). ONE buffer entry PER STEP (not one for the whole editor, unlike
 * `BaseCopyEditor`'s single subject+body pair) because each step's Subject+
 * Body is independently edited and independently debounced — typing in step
 * 2 must not reset step 1's pending commit.
 *
 * Flushed on the debounce timer, on UNMOUNT (card collapse/rail switch —
 * same as `BaseCopyEditor`), and on BLUR: the wrapping `<div onBlur={…}>`
 * below relies on React's synthetic `blur` event bubbling (React's
 * `onBlur`/`onFocus` are implemented over the native `focusout`/`focusin`
 * events, which DO bubble, unlike native `blur`/`focus`) to catch a blur
 * from ANY of `StepOverrideEditor`'s per-step Subject inputs or body
 * editors without adding an `onBlur` prop to that component — the same
 * "blur fires before the Send button's click" guarantee `BaseCopyEditor`
 * documents. Same KNOWN GAP as `BaseCopyEditor` (typing + a hard tab
 * close/reload within the debounce window loses that last edit) and the
 * same deliberate absence of a `beforeunload` hook — see
 * `BaseCopyEditor.tsx`'s `BASE_COPY_COMMIT_MS` doc comment for why that
 * call stays Spencer's.
 *
 * The buffer carries ZERO authority: it exists only to keep controlled
 * inputs from losing keystrokes while a write is pending. Followers,
 * persistence, and Send all read only the staged store — never the buffer,
 * and never anything with the shape or lifetime of the deleted sentinel
 * (a store instance that was itself the storage of record, with its own
 * send-time fan-out).
 *
 * Reuses `StepOverrideEditor` unchanged, in BATCH mode via its `labels`
 * prop (that component's own portability contract). `itemId` takes the
 * local `BATCH_PSEUDO_ITEM_ID` constant below — a seedKey/aria-namespace
 * ingredient only, scoped to this component's own mount, never a key in any
 * store shared with real items and never on any save path (the property
 * that made the deleted sentinel objectionable in the first place — see
 * that constant's own doc comment).
 */

/**
 * Copy strings — REVIEW-THESE at QA (plan Part 2, Open Question 1). Held as
 * one constant so the copy review is a single diff hunk.
 */
export const BATCH_LABELS: StepOverrideEditorLabels = {
  header: 'Flow step defaults',
  headerHint: '— applied to every lead in this batch (optional)',
  subjectHint: '— all leads in this batch',
  bodyHint: 'all leads in this batch',
  // Not copy review — a correctness requirement. A multi-lead Gong card
  // renders this editor AND every open lead row's own one, so without a
  // scope qualifier the accessibility tree carries several buttons named
  // `Expand step 1 — Manual Email` with nothing to tell them apart. See
  // `StepOverrideEditorLabels.stepScope`.
  stepScope: 'all leads in this batch',
};

/**
 * A seedKey/aria-namespace ingredient LOCAL to this component's own mount —
 * `StepOverrideEditor`'s `itemId` prop composes `RichHtmlField`'s per-step
 * seedKey and nothing else (that component's own doc comment). Unlike the
 * deleted sentinel item id, this is never a key in any store shared
 * with real items and never touches any save path — the objectionable
 * property of the sentinel (a magic id living in a real DRAFT STORE, with
 * "defense in depth" leak guards elsewhere in the codebase) does not exist
 * to guard against here, because this component no longer owns a store at
 * all.
 */
const BATCH_PSEUDO_ITEM_ID = -1;

export interface BulkStepDefaultsEditorProps {
  /** `EnrollQueueContext.baseCopyDraftStore.identity` — the same identity every staged-play-config read/write in this card uses. */
  identity: IdentityKey | null;
  /** `playConfigKey(playTypeId, playInstanceRef)` — this group's play key. */
  playKey: PlayKey;
  /** The group's Gong flow id (`PlayQueueCard`'s `gongFlowId`, non-null by the mount gate). */
  flowId: string;
  /** `group.ui.body_format`, threaded straight through to the reused editor. */
  bodyFormat?: 'plain' | 'rich';
  readOnly: boolean;
  /** `sending || groupBusy` — editing batch copy mid-send would race the save phase. */
  busy: boolean;
}

export function BulkStepDefaultsEditor({
  identity,
  playKey,
  flowId,
  bodyFormat = 'plain',
  readOnly,
  busy,
}: BulkStepDefaultsEditorProps) {
  // Key Technical Decision 6 (via U7) — the SAME forced-refetch hook the
  // per-lead editor uses (`EnrollmentItemEditor`'s `GongFlowStepsSection`), so
  // the batch editor and every row editor can never disagree about the
  // flow's current default copy.
  const stepsQuery = useGongFlowStepsForced(flowId);
  // The 200ms-per-step typing buffer, the staged-store read channel and the
  // mount-time `reconcileStagedStepCopy` prune all live in
  // `useBatchStepBuffer` (plan 2026-09-30-002 U3 Step 2) so the V2 Glaze
  // `BulkEditor` drives the SAME implementation rather than a second copy —
  // read that hook's header for the full buffer rationale. `saveErrorFor` is
  // renamed at the destructure to its long-standing local name, which
  // documents that batch scope has no save errors to show.
  const {
    localDraftFor,
    subscribeStep,
    saveErrorFor: noSaveError,
    handleChangeBatchStep,
    handleResetBatchStep,
    flushAllPending,
  } = useBatchStepBuffer({ identity, playKey, liveSteps: stepsQuery.data?.steps });

  // Same posture as `GongFlowStepsSection` (`EnrollmentItemEditor.tsx`): a
  // steps-endpoint failure renders a note and stops there rather than
  // breaking the rest of the Bulk Editor. Copy and markup are deliberately
  // byte-identical to that section's so the two cannot drift.
  if (stepsQuery.isLoading) return null;
  if (stepsQuery.isError || !stepsQuery.data) {
    return (
      <div
        role="alert"
        className="mt-2 text-[11px] rounded-md border border-content-danger/30 bg-content-danger/5 px-2 py-1 text-content-danger"
      >
        Flow steps unavailable — try again shortly.
      </div>
    );
  }

  return (
    // See the header — this relies on React's synthetic `blur` bubbling
    // (backed by the native `focusout` event, which DOES bubble) to flush
    // every step's pending buffer the moment focus leaves ANY of this
    // editor's fields, with no change to `StepOverrideEditor` itself.
    <div onBlur={flushAllPending}>
      <StepOverrideEditor
        itemId={BATCH_PSEUDO_ITEM_ID}
        steps={stepsQuery.data.steps}
        // No persisted overrides exist for a batch — the middle rung of the
        // row's `localDraft ?? persistedOverride ?? batchDraft ?? stepDefault`
        // derivation is per-lead server state, which a batch default has none of.
        overrides={[]}
        disabled={readOnly || busy}
        onChangeOverride={handleChangeBatchStep}
        onResetOverride={handleResetBatchStep}
        localDraftFor={localDraftFor}
        saveErrorFor={noSaveError}
        subscribeStep={subscribeStep}
        bodyFormat={bodyFormat}
        labels={BATCH_LABELS}
      />
    </div>
  );
}
